import React, { useMemo, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Box,
  Avatar,
  Tooltip,
  CircularProgress,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import { Refresh, Edit, Add, Delete } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Title from '../../components/Title';
import { useGetAdminRolesQuery, useDeleteAdminRoleMutation } from '../../redux/api/adminRolesApi';
import { AdminRole } from '../../resources/types/adminRolesTypes';
import AdminRoleDialog from './AdminRoleDialog';
import { getRoleIcon } from './roleIcons';

const PREFIX = 'AdminRolesPage';

const classes = {
  header: `${PREFIX}-header`,
  search: `${PREFIX}-search`,
  permissionsCell: `${PREFIX}-permissionsCell`,
  emptyState: `${PREFIX}-emptyState`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.header}`]: {
    marginBottom: 20
  },
  [`& .${classes.search}`]: {
    marginBottom: 20,
    width: 300
  },
  [`& .${classes.permissionsCell}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    maxWidth: 500
  },
  [`& .${classes.emptyState}`]: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  }
}));

const AdminRolesPage = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState<AdminRole | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<AdminRole | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  const { data: roles = [], isLoading, refetch, isFetching } = useGetAdminRolesQuery();
  const [deleteRole, { isLoading: isDeleting }] = useDeleteAdminRoleMutation();

  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return roles;
    
    const query = searchQuery.toLowerCase();
    return roles.filter(role => 
      role.name?.toLowerCase().includes(query)
    );
  }, [roles, searchQuery]);

  const handleRefresh = () => {
    refetch();
  };

  const handleOpenDialog = useCallback(() => {
    setEditRole(null);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditRole(null);
  }, []);

  const handleEditRole = useCallback((role: AdminRole) => {
    setEditRole(role);
    setDialogOpen(true);
  }, []);

  const handleOpenDeleteConfirm = useCallback((role: AdminRole) => {
    setRoleToDelete(role);
    setDeleteConfirmOpen(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
    setRoleToDelete(null);
  }, []);

  const handleDeleteRole = useCallback(async () => {
    if (!roleToDelete) return;

    try {
      await deleteRole(roleToDelete._id).unwrap();
      setSnackbarMessage('Роль видалено');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
      refetch();
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Помилка при видаленні ролі';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [roleToDelete, deleteRole, refetch]);

  const handleSuccess = useCallback((message?: string) => {
    setSnackbarMessage(message || (editRole ? 'Роль оновлено' : 'Роль успішно створено!'));
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    refetch();
  }, [editRole, refetch]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  if (isLoading) {
    return (
      <Root>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Root>
    );
  }

  return (
    <Root>
      <Grid
        container
        alignItems='center'
        className={classes.header}
        spacing={2}
      >
        <Grid item>
          <Title>Ролі адміністраторів</Title>
        </Grid>
        <Grid item>
          <Button
            color='primary'
            startIcon={<Add />}
            variant='contained'
            onClick={handleOpenDialog}
          >
            Нова роль
          </Button>
        </Grid>
        <Grid item>
          <Button
            startIcon={isFetching ? <CircularProgress size={20} /> : <Refresh />}
            variant='outlined'
            onClick={handleRefresh}
            disabled={isFetching}
            sx={{
              color: 'text.primary',
              borderColor: 'text.primary',
              '&:hover': {
                borderColor: 'text.primary',
                backgroundColor: 'action.hover'
              }
            }}
          >
            Оновити
          </Button>
        </Grid>
      </Grid>

      <TextField
        className={classes.search}
        label="Пошук по назві ролі"
        variant="outlined"
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        fullWidth={false}
      />

      {filteredRoles.length === 0 ? (
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6">
              {searchQuery ? 'Ролей не знайдено за вашим запитом' : 'Ролей адміністраторів не знайдено'}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Іконка</TableCell>
                <TableCell>Назва ролі</TableCell>
                <TableCell>Кількість користувачів</TableCell>
                <TableCell>Дозволи (Permissions)</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.map(role => {
                const RoleIcon = getRoleIcon(role.icon);
                return (
                <TableRow key={role._id}>
                  <TableCell>
                    <Tooltip title={role.name} arrow>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 213, 79, 0.12)' 
                            : 'rgba(66, 66, 66, 0.12)',
                          borderRadius: '8px'
                        }}
                      >
                        <RoleIcon sx={{ 
                          fontSize: 24, 
                          color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242' 
                        }} />
                      </Avatar>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <strong>{role.name}</strong>
                  </TableCell>
                  <TableCell>
                    {role.userCount ?? 0}
                  </TableCell>
                  <TableCell>
                    <Box className={classes.permissionsCell}>
                      {role.tabs && role.tabs.length > 0 ? (
                        role.tabs.map((tab) => (
                          <Chip
                            key={`${role._id}-${tab._id}`}
                            label={`${tab.name} (${tab.code})`}
                            size="small"
                            variant="outlined"
                            sx={(theme) => ({
                              borderColor: theme.palette.mode === 'dark' ? '#5073F8' : '#424242',
                              color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242'
                            })}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Немає дозволів
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align='right'>
                    <Tooltip title="Редагувати" arrow>
                      <IconButton 
                        size='medium'
                        onClick={() => handleEditRole(role)}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                          borderRadius: '8px',
                          marginRight: 1,
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Видалити" arrow>
                      <IconButton 
                        size='medium'
                        onClick={() => handleOpenDeleteConfirm(role)}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: 'error.main',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AdminRoleDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleSuccess}
        editRole={editRole}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
      >
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете видалити роль "{roleToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={isDeleting}>
            Скасувати
          </Button>
          <Button 
            onClick={handleDeleteRole} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Видалення...' : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Root>
  );
};

export default AdminRolesPage;
